import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useAzureAuthSignOut } from '../../../../../context/AzureAuth/hooks/useAzureAuthSignOut';
import { useUrqlContext } from '../../../../../context/Urql/UrqlProvider/UrqlContext';
import { AccountMenuPrimaryItem } from '../AccountMenuPrimaryItem';
import { AccountMenuSignOutItemEmbeddedModal } from './components/AccountMenuSignOutItemEmbeddedModal';

// ─────────────────────────────────────────────────────────────────────────────

export const AccountMenuSignOutItem = (props: AccountMenuSignOutProps) => {
  const { isSigningOut, resetNavigation } = props;

  const { resetClient } = useUrqlContext();

  /**
   * Reset GraphQL client and navigation state.
   */
  const resetAppState = useCallback(async () => {
    await resetClient();
    resetNavigation();
  }, [resetClient, resetNavigation]);

  const signOutFromAzure = useAzureAuthSignOut({ resetAppState });

  // ─────────────────────────────────────────────────────────────────────

  const { formatMessage } = useIntl();

  const isAuthHybridFlowEnabled = useFeatureFlag('cels-2041-auth-hybrid-flow');

  // ─── State ───────────────────────────────────────────────────────────

  const [isSignOutModalOpen, setIsSignOutModalOpen] = useState(false);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const openSignOutModal = useCallback(() => {
    setIsSignOutModalOpen(true);
  }, []);

  const closeSignOutModal = useCallback(() => {
    setIsSignOutModalOpen(false);
  }, []);

  const handleSignOut = useCallback(async () => {
    if (isAuthHybridFlowEnabled) {
      await signOutFromAzure();

      return;
    }

    openSignOutModal();
  }, [isAuthHybridFlowEnabled, openSignOutModal, signOutFromAzure]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <AccountMenuPrimaryItem
        testID="account.sidebar-tab.sign-out"
        label={formatMessage(messages.accountSidebarSignOut)}
        iconName="IconLogout"
        disabled={isSigningOut}
        onPress={handleSignOut}
      />

      {isAuthHybridFlowEnabled ? null : (
        <AccountMenuSignOutItemEmbeddedModal
          isVisible={isSignOutModalOpen}
          closeModal={closeSignOutModal}
          resetAppState={resetAppState}
        />
      )}
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountSidebarSignOut: {
    defaultMessage: 'Sign out',
    description: 'Account | Sidebar | Sign Out',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type AccountMenuSignOutProps = Readonly<{
  isSigningOut: boolean;
  resetNavigation: () => void;
}>;
